import { useLocation, useNavigate } from "react-router-dom";
import { Storefront, MagnifyingGlass, ArrowRight } from "@phosphor-icons/react";
import { useAuth } from "../../lib/auth";
import { useCustomerAuth } from "../../lib/customerAuth";

/**
 * Sprint H · Batch 7 — Sticky CTA bar inferior para móvil.
 *
 * Visible SOLO en viewport < md (768 px). Adapta el botón al contexto:
 *  - Landing / páginas estáticas → "Buscar Profesional · Marketplace"
 *  - Dentro de /marketplace → "Acceso Talleres · Empezar Gratis"
 *
 * Se auto-oculta cuando:
 *  - El usuario está logueado en el panel del taller (`user`).
 *  - El cliente final está en su área (/mi-cuenta).
 *  - Está en pantallas de booking/checkout (`/reservar/:slug` paso final,
 *    `/cita/*`, `/billing/*`) para no tapar el formulario.
 *  - Está en flujo auth-sensible (login/forgot/reset).
 *
 * Se monta una sola vez a nivel global (App.js).
 */
const HIDDEN_PREFIXES = [
  "/login", "/register",
  "/cliente/login", "/cliente/signup", "/cliente/recuperar-password",
  "/forgot-password", "/reset-password",
  "/mi-cuenta",
  "/cita/",
  "/billing/",
  "/admin",
  // Sprint UX-1 · Fase 2E P1-2E-1 · superficies de conversión titular
  // no deben mostrar cross-CTA hacia flow cliente
  "/pricing", "/precios", "/landing", "/home", "/welcome",
];

export default function StickyMobileActions() {
  const { user } = useAuth() || {};
  const { account } = useCustomerAuth() || {};
  const location = useLocation();
  const navigate = useNavigate();

  // Oculto si user logueado (taller) — el panel ya tiene su propia UX
  if (user) return null;

  // Oculto en rutas sensibles / flow de booking ya iniciado
  const path = location.pathname;
  if (HIDDEN_PREFIXES.some((p) => path.startsWith(p))) return null;

  // /reservar/:slug se permite — pero ocultamos si el usuario está activamente
  // rellenando el formulario (detectamos el step ≥ 3 por URL query o por DOM).
  // Por simplicidad ocultamos también si está autenticado como customer.
  if (account && (path.startsWith("/reservar/") || path === "/marketplace")) {
    return null;
  }

  // Decide CTA según ubicación
  const onMarketplace = path.startsWith("/marketplace");
  const onPublicBooking = path.startsWith("/reservar/");

  // En public booking ocultamos también el sticky — el usuario ya está en el funnel
  if (onPublicBooking) return null;

  const cta = onMarketplace
    ? {
        label: "Acceso Talleres · Empezar Gratis",
        icon: Storefront,
        onClick: () => navigate("/register"),
        testId: "sticky-cta-pro",
        ariaLabel: "Soy profesional, quiero registrar mi centro",
      }
    : {
        label: "Buscar Profesional · Marketplace",
        icon: MagnifyingGlass,
        onClick: () => navigate("/marketplace"),
        testId: "sticky-cta-marketplace",
        ariaLabel: "Buscar profesional en el Marketplace",
      };

  return (
    <div
      className="md:hidden fixed bottom-0 left-0 right-0 z-[50] pointer-events-none"
      data-testid="sticky-mobile-actions"
      style={{ paddingBottom: "calc(env(safe-area-inset-bottom) + 0.5rem)" }}
    >
      {/* Backdrop con blur — usa CSS vars del tema activo */}
      <div className="ag-sticky-cta-shell pointer-events-auto mx-3 mb-2 rounded-2xl border border-[var(--ag-border)] bg-[var(--ag-midnight-soft)]/85 backdrop-blur-md shadow-[0_-8px_30px_rgba(0,0,0,0.35)]">
        <button
          type="button"
          onClick={cta.onClick}
          aria-label={cta.ariaLabel}
          data-testid={cta.testId}
          className="w-full px-4 py-3.5 flex items-center justify-between gap-3 rounded-2xl text-[#1A1A1A] bg-[var(--ag-gold)] hover:bg-[#FFC73B] active:scale-[0.98] transition-all font-display font-bold tracking-tight text-base"
        >
          <span className="flex items-center gap-2.5">
            <cta.icon size={20} weight="fill" />
            <span>{cta.label}</span>
          </span>
          <ArrowRight size={18} weight="bold" />
        </button>
      </div>
    </div>
  );
}
